/* ==========================================================================
   SHEHAR — Hero slider behaviour
   Custom element, no dependencies. Registers once even if the script tag is
   included by several sections.
   ========================================================================== */
(function () {
  'use strict';

  if (window.customElements && customElements.get('shehar-hero-slider')) return;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DESKTOP = '(min-width: 741px)';

  function SheharHeroSliderFactory() {
    return class SheharHeroSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.prototype.slice.call(this.querySelectorAll('.shehar-hero__slide'));
        this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-shehar-hero-dot]'));
        this.toggle = this.querySelector('[data-shehar-hero-toggle]');
        this.index = 0;
        this.timer = null;
        this.paused = false;

        if (!this.slides.length) return;

        this.interval = parseInt(this.getAttribute('data-interval'), 10) || 7000;
        this.autoplay =
          this.getAttribute('data-autoplay') === 'true' &&
          this.slides.length > 1 &&
          !reduceMotion.matches;

        this.style.setProperty('--shehar-hero-progress', this.interval + 'ms');

        this.setSlideFocusability();
        this.loadVideo(this.slides[0]);

        if (this.slides.length > 1) {
          this.bindEvents();
          if (this.autoplay) this.play();
        }
      }

      disconnectedCallback() {
        this.stop();
      }

      bindEvents() {
        var self = this;

        this.dots.forEach(function (dot) {
          dot.addEventListener('click', function () {
            self.goTo(parseInt(dot.getAttribute('data-shehar-hero-dot'), 10), true);
          });
        });

        if (this.toggle) {
          this.toggle.addEventListener('click', function () {
            self.paused ? self.play() : self.pause();
          });
        }

        // Pause while the visitor is reading or tabbing through.
        this.addEventListener('mouseenter', function () { self.stop(); });
        this.addEventListener('mouseleave', function () { if (!self.paused) self.play(); });
        this.addEventListener('focusin', function () { self.stop(); });
        this.addEventListener('focusout', function (e) {
          if (!self.contains(e.relatedTarget) && !self.paused) self.play();
        });

        // Don't burn cycles or advance unseen slides in a background tab.
        this.onVisibility = function () {
          document.hidden ? self.stop() : (self.paused ? null : self.play());
        };
        document.addEventListener('visibilitychange', this.onVisibility);

        this.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') { e.preventDefault(); self.goTo(self.index + 1, true); }
          if (e.key === 'ArrowLeft') { e.preventDefault(); self.goTo(self.index - 1, true); }
        });

        // Swipe. Pointer events cover touch and pen without a library.
        var startX = null;
        this.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse') return;
          startX = e.clientX;
        }, { passive: true });

        this.addEventListener('pointerup', function (e) {
          if (startX === null) return;
          var delta = e.clientX - startX;
          startX = null;
          if (Math.abs(delta) < 45) return;
          self.goTo(delta < 0 ? self.index + 1 : self.index - 1, true);
        }, { passive: true });
      }

      goTo(next, userInitiated) {
        var total = this.slides.length;
        var target = ((next % total) + total) % total;
        if (target === this.index) return;

        this.slides[this.index].classList.remove('is-active');
        this.slides[this.index].setAttribute('aria-hidden', 'true');
        this.pauseVideo(this.slides[this.index]);

        this.index = target;

        this.slides[target].classList.add('is-active');
        this.slides[target].removeAttribute('aria-hidden');
        this.loadVideo(this.slides[target]);

        this.dots.forEach(function (dot, i) {
          dot.classList.toggle('is-active', i === target);
          i === target ? dot.setAttribute('aria-current', 'true') : dot.removeAttribute('aria-current');
        });

        this.setSlideFocusability();

        // A manual choice should stop the carousel taking over again.
        if (userInitiated) {
          this.pause();
        } else if (this.autoplay && !this.paused) {
          this.restartProgress();
        }
      }

      // Hidden slides must not be reachable by keyboard.
      setSlideFocusability() {
        var active = this.index;
        this.slides.forEach(function (slide, i) {
          var focusables = slide.querySelectorAll('a[href], button');
          Array.prototype.forEach.call(focusables, function (el) {
            if (i === active) {
              el.removeAttribute('tabindex');
            } else {
              el.setAttribute('tabindex', '-1');
            }
          });
        });
      }

      restartProgress() {
        var dot = this.dots[this.index];
        if (!dot) return;
        var fill = dot.querySelector('.shehar-hero__dot-fill');
        if (!fill) return;
        this.classList.remove('shehar-hero--autoplaying');
        fill.style.transform = 'scaleX(0)';
        void fill.offsetWidth; // force reflow so the transition replays
        this.classList.add('shehar-hero--autoplaying');
        fill.style.transform = '';
      }

      play() {
        if (!this.autoplay) return;
        var self = this;
        this.paused = false;
        this.classList.add('shehar-hero--autoplaying');
        if (this.toggle) this.toggle.setAttribute('aria-pressed', 'false');
        this.stop();
        this.restartProgress();
        this.timer = window.setInterval(function () {
          self.goTo(self.index + 1, false);
        }, this.interval);
      }

      pause() {
        this.paused = true;
        this.classList.remove('shehar-hero--autoplaying');
        if (this.toggle) this.toggle.setAttribute('aria-pressed', 'true');
        this.stop();
      }

      stop() {
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      }

      // Only the active slide's video is fetched, and only the source matching
      // the current viewport — never both files.
      loadVideo(slide) {
        var video = slide.querySelector('.shehar-hero__video');
        if (!video || reduceMotion.matches) return;

        if (!video.src) {
          var desktop = video.getAttribute('data-desktop-src');
          var mobile = video.getAttribute('data-mobile-src');
          var src = window.matchMedia(DESKTOP).matches ? desktop || mobile : mobile || desktop;
          if (!src) return;

          video.addEventListener('canplay', function () {
            video.classList.add('is-playing');
          }, { once: true });

          video.src = src;
          video.load();
        }

        var attempt = video.play();
        if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
      }

      pauseVideo(slide) {
        var video = slide.querySelector('.shehar-hero__video');
        if (video && !video.paused) video.pause();
      }
    };
  }

  if (window.customElements) {
    customElements.define('shehar-hero-slider', SheharHeroSliderFactory());
  }
})();
